import React, { useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { base_api_url } from "../../../environment.jsx";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { X, Plus, Minus, Check } from "lucide-react";
import "./AdjustStockModal.css";

const AdjustStockModal = ({ show, onClose, onSuccess, ingredient }) => {
    const MySwal = withReactContent(Swal);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("AJOUT"); // "AJOUT" ou "RETRAIT"
    const [formData, setFormData] = useState({
        quantite: "",
        motif: "",
        note: ""
    });

    const token = JSON.parse(localStorage.getItem("user"))?.access_token;

    // Motifs pour l'ajout de stock
    const motifsAjout = [
        "Réception",
        "Retour",
        "Correction inventaire",
        "Autre"
    ];

    // Motifs pour le retrait de stock
    const motifsRetrait = [
        "Perte",
        "Casse",
        "Offert / Dégustation",
        "Erreur",
        "Autre"
    ];

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setFormData({
            quantite: "",
            motif: "",
            note: ""
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const resetForm = () => {
        setFormData({
            quantite: "",
            motif: "",
            note: ""
        });
        setActiveTab("AJOUT");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.quantite || parseFloat(formData.quantite) <= 0) {
            MySwal.fire({
                icon: "error",
                text: "Veuillez entrer une quantité valide.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                    popup: "pixel-toast",
                },
            });
            return;
        }

        // Vérifier qu'un motif est sélectionné
        if (!formData.motif) {
            MySwal.fire({
                icon: "error",
                text: "Veuillez sélectionner un motif.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                    popup: "pixel-toast",
                },
            });
            return;
        }

        setLoading(true);

        try {
            const payload = {
                id_ingredient: ingredient.id_ingredient,
                quantite: parseFloat(formData.quantite),
                type_mouvement: activeTab, // "AJOUT" ou "RETRAIT"
                commentaire: formData.note 
                    ? `${formData.motif} - ${formData.note}` 
                    : formData.motif
            };

            console.log("Submitting stock adjustment:", payload);

            const response = await axios.post(
                `${base_api_url}api/stock/mouvement`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("Stock adjusted successfully:", response.data);

            // Show success toast
            MySwal.fire({
                icon: "success",
                text: activeTab === "AJOUT"
                    ? `${formData.quantite} ${ingredient.unite} ajouté(s) au stock.`
                    : `${formData.quantite} ${ingredient.unite} retiré(s) du stock.`,
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                    popup: "pixel-toast",
                },
            });

            resetForm();
            onSuccess(); // Refresh the ingredient data
            onClose();

        } catch (error) {
            console.error("Error adjusting stock:", error);

            let errorMessage = "Une erreur s'est produite lors de l'ajustement du stock.";

            if (error.response) {
                const status = error.response.status;
                const errorData = error.response.data;

                switch (status) {
                    case 400:
                        errorMessage = errorData?.message || "Données invalides. Vérifiez les champs.";
                        break;
                    case 401:
                        errorMessage = "Session expirée. Veuillez vous reconnecter.";
                        break;
                    case 404:
                        errorMessage = "L'ingrédient n'existe pas.";
                        break;
                    case 500:
                        errorMessage = "Erreur serveur. Veuillez réessayer.";
                        break;
                    default:
                        errorMessage = errorData?.message || `Erreur ${status} lors de l'opération.`;
                }
            } else if (error.request) {
                errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion.";
            }

            MySwal.fire({
                icon: "error",
                text: errorMessage,
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
                customClass: {
                    popup: "pixel-toast",
                },
            });
        } finally {
            setLoading(false);
        }
    };

    if (!show || !ingredient) return null;

    const currentMotifs = activeTab === "AJOUT" ? motifsAjout : motifsRetrait;

    return (
        <>
            {/* Backdrop */}
            <div
                className={`adjust-stock-backdrop ${show ? 'show' : ''}`}
                onClick={onClose}
            ></div>

            {/* Modal */}
            <div className={`adjust-stock-modal ${show ? 'show' : ''}`}>
                <form onSubmit={handleSubmit}>
                    {/* Header */}
                    <div className="adjust-stock-header">
                        <h4>Ajuster le stock</h4>
                        <button
                            type="button"
                            className="adjust-stock-close-btn"
                            onClick={onClose}
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Ingredient Info */}
                    <div className="adjust-stock-info">
                        <span className="adjust-stock-info-name">{ingredient.nom}</span>
                        <span className="adjust-stock-info-text"> — Stock actuel : </span>
                        <span className="adjust-stock-info-quantity">
                            {ingredient.stock?.quantite || 0} {ingredient.unite}
                        </span>
                    </div>

                    {/* Body */}
                    <div className="adjust-stock-body">
                        {/* Tabs */}
                        <div className="adjust-stock-tabs">
                            <button
                                type="button"
                                className={`adjust-stock-tab ${activeTab === "AJOUT" ? "active" : ""}`}
                                onClick={() => handleTabChange("AJOUT")}
                            >
                                <Plus size={18} />
                                Ajouter
                            </button>
                            <button
                                type="button"
                                className={`adjust-stock-tab ${activeTab === "RETRAIT" ? "active" : ""}`}
                                onClick={() => handleTabChange("RETRAIT")}
                            >
                                <Minus size={18} />
                                Retirer
                            </button>
                        </div>

                        {/* Quantité */}
                        <div className="adjust-stock-form-group">
                            <label className="adjust-stock-label adjust-stock-label-required">
                                Quantité ({ingredient.unite})
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                name="quantite"
                                value={formData.quantite}
                                onChange={handleChange}
                                required
                                className="adjust-stock-input"
                                placeholder="0"
                                min="0.01"
                            />
                        </div>

                        {/* Motif */}
                        <div className="adjust-stock-form-group">
                            <label className="adjust-stock-label adjust-stock-label-required">
                                Motif
                            </label>
                            <select
                                name="motif"
                                value={formData.motif}
                                onChange={handleChange}
                                required
                                className="adjust-stock-select"
                            >
                                <option value="">Sélectionner un motif</option>
                                {currentMotifs.map((motif) => (
                                    <option key={motif} value={motif}>
                                        {motif}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Note */}
                        <div className="adjust-stock-form-group">
                            <label className="adjust-stock-label">Note (optionnel)</label>
                            <textarea
                                name="note"
                                value={formData.note}
                                onChange={handleChange}
                                className="adjust-stock-textarea"
                                placeholder="Informations complémentaires..."
                                rows="3"
                            />
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="adjust-stock-footer">
                        <button
                            type="button"
                            className="adjust-stock-btn adjust-stock-btn-secondary"
                            onClick={onClose}
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="adjust-stock-btn adjust-stock-btn-primary"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="adjust-stock-spinner"></span>
                                    Traitement...
                                </>
                            ) : (
                                <>
                                    <Check size={18} />
                                    Confirmer
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

AdjustStockModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    ingredient: PropTypes.object,
};

export default AdjustStockModal;
